import { Link } from 'react-router';
import {
  Activity,
  BarChart3,
  Target,
  DollarSign,
  Bot,
  Plus,
  BookOpen,
  Upload,
  Server,
  Rocket,
  Trophy,
  ArrowRight,
} from 'lucide-react';
import { useBots } from '@/hooks/use-bots';
import { useStrategies } from '@/hooks/use-strategies';
import { useMt5Accounts } from '@/hooks/use-accounts';
import { useChallenges } from '@/hooks/use-challenges';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { OnboardingChecklist } from '@/components/onboarding-checklist';
import type { OnboardingStep } from '@/components/onboarding-checklist';
import { formatCurrency, formatPnl, formatPct, pnlColor, botCurrency } from '@/lib/format';
import { useCurrency } from '@/lib/currency';
import type { BotInstance, Challenge } from '@/types/api';

export default function DashboardPage() {
  const { homeCurrency, convert } = useCurrency();
  const { data: bots, isLoading: botsLoading, error } = useBots({ refetchInterval: 10_000 });
  const { data: strategies, isLoading: strategiesLoading } = useStrategies({ exclude: 'ARCHIVED' });
  const { data: mt5Accounts, isLoading: accountsLoading } = useMt5Accounts();
  const { data: challenges } = useChallenges();

  if (botsLoading || strategiesLoading || accountsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="glass-sm rounded-lg p-6 border-red/20 text-center">
        <p className="text-red text-sm font-semibold mb-2">Failed to load dashboard</p>
        <p className="text-xs text-ink-3">Check that the API server is running on port 3000.</p>
      </div>
    );
  }

  const allBots = bots ?? [];
  const allStrategies = strategies ?? [];
  const allAccounts = mt5Accounts ?? [];
  const liveBots = allBots.filter((b) => b.botMode === 'LIVE');
  const activeBots = allBots.filter((b) => b.is_active);

  const onboardingSteps: OnboardingStep[] = [
    {
      label: 'Create a strategy',
      description: 'Upload your trade history or build a strategy from scratch.',
      done: allStrategies.length > 0,
      href: '/strategies/new',
      icon: <BookOpen size={16} />,
    },
    {
      label: 'Publish your strategy',
      description: 'Backtest and publish your strategy to lock in the rules.',
      done: allStrategies.some((s) => s.status === 'PUBLISHED'),
      href: '/strategies',
      icon: <Upload size={16} />,
    },
    {
      label: 'Connect MT5 account',
      description: 'Link your MetaTrader 5 broker account for live trading.',
      done: allAccounts.length > 0,
      href: '/accounts/new',
      icon: <Server size={16} />,
    },
    {
      label: 'Create your first bot',
      description: 'Deploy a bot using your published strategy.',
      done: allBots.length > 0,
      href: '/bots/new',
      icon: <Bot size={16} />,
    },
    {
      label: 'Start your bot',
      description: 'Activate your bot to begin trading.',
      done: activeBots.length > 0 || allBots.some((b) => b.total_trades > 0),
      href: '/bots',
      icon: <Rocket size={16} />,
    },
  ];
  const allComplete = onboardingSteps.every((s) => s.done);

  // Aggregations — only LIVE bots contribute to portfolio stats
  const totalEquity = liveBots.reduce((sum, b) => sum + convert(b.virtual_equity, botCurrency(b)), 0);
  const totalPnl = liveBots.reduce((sum, b) => sum + convert(b.combined_pnl ?? b.total_pnl, botCurrency(b)), 0);
  const totalCapital = liveBots.reduce((sum, b) => sum + convert(b.assigned_capital, botCurrency(b)), 0);
  const pnlPct = totalCapital > 0 ? (totalPnl / totalCapital) * 100 : 0;
  const totalTrades = liveBots.reduce((sum, b) => sum + b.total_trades, 0);
  const totalWins = liveBots.reduce((sum, b) => sum + b.winning_trades, 0);
  const winRate = totalTrades > 0 ? (totalWins / totalTrades) * 100 : 0;

  return (
    <div className="space-y-6">
      {/* Hero Card */}
      <div className="glass-sm rounded-lg p-6">
        <p className="text-xs font-semibold text-ink-3 uppercase tracking-wider mb-1">
          Portfolio Value
        </p>
        <div className="flex items-baseline gap-3 mb-1">
          <span className="text-3xl font-extrabold font-mono tracking-tight">
            {formatCurrency(totalEquity, homeCurrency)}
          </span>
          <span className={`text-sm font-bold font-mono ${pnlColor(totalPnl)}`}>
            {formatPnl(totalPnl)}
          </span>
        </div>
        <p className={`text-xs font-semibold ${pnlColor(pnlPct)}`}>
          {formatPct(pnlPct)} all time
        </p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          icon={<Activity size={16} className="text-orange" />}
          label="Active Bots"
          value={String(activeBots.length)}
        />
        <StatCard
          icon={<BarChart3 size={16} className="text-blue" />}
          label="Total Trades"
          value={String(totalTrades)}
        />
        <StatCard
          icon={<Target size={16} className="text-violet" />}
          label="Win Rate"
          value={`${winRate.toFixed(1)}%`}
        />
        <StatCard
          icon={<DollarSign size={16} className="text-green" />}
          label="Total P&L"
          value={formatPnl(totalPnl, homeCurrency)}
          valueColor={pnlColor(totalPnl)}
        />
      </div>

      {/* Challenge Widget */}
      <ChallengeWidget challenges={challenges ?? []} />

      {/* Active Bots List — replaced by onboarding checklist until all steps done */}
      {!allComplete ? (
        <OnboardingChecklist steps={onboardingSteps} />
      ) : (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-extrabold">
              {activeBots.length > 0 ? 'Active Bots' : 'Your Bots'}
            </h2>
            <Link to="/bots">
              <Button variant="ghost" size="sm">
                View All
              </Button>
            </Link>
          </div>

          {allBots.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="space-y-2">
              {(activeBots.length > 0 ? activeBots : allBots)
                .slice(0, 5)
                .map((bot) => (
                  <BotRow key={bot.id} bot={bot} />
                ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Sub-components ────────────────────────────────

function StatCard({
  icon,
  label,
  value,
  valueColor,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  valueColor?: string;
}) {
  return (
    <div className="glass-sm rounded-lg p-4 hover:border-orange/15 transition-colors">
      <div className="flex items-center gap-2 mb-2">{icon}</div>
      <p className={`text-lg font-extrabold font-mono tracking-tight ${valueColor ?? ''}`}>
        {value}
      </p>
      <p className="text-[10px] font-semibold text-ink-4 uppercase tracking-wider mt-0.5">
        {label}
      </p>
    </div>
  );
}

function BotRow({ bot }: { bot: BotInstance }) {
  const currency = botCurrency(bot);
  const isPaper = bot.botMode === 'PAPER';
  const pnl = bot.combined_pnl ?? bot.total_pnl;
  const pnlPctValue =
    bot.assigned_capital > 0 ? (pnl / bot.assigned_capital) * 100 : 0;

  return (
    <Link
      to={`/bots/${bot.id}`}
      className={`group block rounded-lg overflow-hidden transition-all`}
    >
      <div className={`flex border border-glass-border rounded-lg glass-sm hover:border-orange/15 transition-all ${
        bot.is_active
          ? isPaper ? 'border-l-[3px] border-l-blue' : 'border-l-[3px] border-l-green'
          : 'border-l-[3px] border-l-ink-4'
      }`}>
        <div className="flex-1 min-w-0 p-4 space-y-2">
          {/* Row 1 — Name + stats */}
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-bold truncate group-hover:text-orange transition-colors">{bot.name}</span>
            <div className="flex items-center gap-2 text-[10px] text-ink-4 flex-shrink-0">
              <span className="flex items-center gap-1">
                <BarChart3 size={10} />
                {bot.total_trades}
              </span>
              <span>·</span>
              <span className="flex items-center gap-1">
                <Target size={10} />
                {bot.total_trades > 0 ? ((bot.winning_trades / bot.total_trades) * 100).toFixed(0) : 0}%
              </span>
            </div>
          </div>

          {/* Row 2 — Badges */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {bot.is_active ? (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded text-green bg-green-bg border border-green/20">
                <span className="w-1.5 h-1.5 rounded-full bg-green animate-pulse" />
                Running
              </span>
            ) : (
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded text-ink-4 bg-surface-2 border border-glass-border">
                Stopped
              </span>
            )}
            {isPaper ? (
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded text-blue bg-blue-bg border border-blue/20">
                Paper
              </span>
            ) : (
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded text-green bg-green-bg border border-green/20">
                Live
              </span>
            )}
            <Badge color="blue">{bot.symbol}</Badge>
          </div>

          {/* Row 3 — Equity + P&L */}
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-[11px] font-mono text-ink-3">
              {formatCurrency(bot.virtual_equity, currency)} <span className="text-ink-4">equity</span>
            </span>
            <span className={`text-sm font-bold font-mono ${pnlColor(pnl)}`}>
              {formatPnl(pnl, currency)}
            </span>
            <span className={`text-[10px] font-semibold -ml-1.5 ${pnlColor(pnlPctValue)}`}>
              ({formatPct(pnlPctValue)})
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}

function ChallengeWidget({ challenges }: { challenges: Challenge[] }) {
  const activeChallenges = challenges.filter((c) => c.status === 'ACTIVE');

  if (activeChallenges.length > 0) {
    return (
      <div className="glass-sm rounded-lg p-5 border border-orange/10">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Trophy size={16} className="text-orange" />
            <span className="text-sm font-bold">
              {activeChallenges.length === 1 ? 'Active Challenge' : `${activeChallenges.length} Active Challenges`}
            </span>
          </div>
          <Link to="/challenges" className="text-[10px] text-ink-4 hover:text-orange transition-colors flex items-center gap-1">
            View All <ArrowRight size={10} />
          </Link>
        </div>
        <div className="space-y-3">
          {activeChallenges.map((c) => {
            const m = c.metrics;
            const returnPct = m?.return_pct
              ?? ((c.bot?.virtual_equity ?? c.starting_capital) - c.starting_capital)
                / c.starting_capital * 100;
            const startedAt = c.started_at ? new Date(c.started_at).getTime() : Date.now();
            const daysElapsed = Math.floor((Date.now() - startedAt) / 86_400_000);
            const daysRemaining = m?.days_remaining ?? Math.max(0, c.duration_days - daysElapsed);
            const progressPct = m?.progress_to_target_pct
              ?? Math.min(100, Math.max(0, (returnPct / c.profit_target_pct) * 100));

            return (
              <Link key={c.id} to={`/challenges/${c.id}`} className="block group">
                <div className="flex items-center justify-between text-[11px] mb-1">
                  <span className="font-semibold text-ink">{c.tier} · {c.bot?.symbol ?? 'N/A'}</span>
                  <span className="text-ink-4">{daysRemaining}d left</span>
                </div>
                <div className="w-full h-2 bg-surface rounded-full overflow-hidden mb-1">
                  <div
                    className="h-full rounded-full transition-all duration-700"
                    style={{
                      width: `${Math.min(100, Math.max(0, progressPct))}%`,
                      background: 'linear-gradient(90deg, #f97316, #f59e0b)',
                    }}
                  />
                </div>
                <div className="flex items-center justify-between text-[10px]">
                  <span className="text-ink-3">
                    {returnPct >= 0 ? '+' : ''}{returnPct.toFixed(1)}% return
                  </span>
                  <span className="font-bold text-orange group-hover:text-orange/80 transition-colors">
                    {progressPct.toFixed(0)}% to target
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    );
  }

  // Promo / CTA banner — show for users with no challenges OR no active challenge
  const hasPending = challenges.some((c) => c.status === 'PENDING');
  const hasChallenges = challenges.length > 0;
  const ctaText = hasPending
    ? 'You have a pending challenge — activate it to start'
    : !hasChallenges
      ? 'Enter the ForgeAlpha Challenge — win up to $500 cash'
      : 'Start a new challenge and prove your edge';

  return (
    <div
      className="rounded-lg p-5 hover:brightness-110 transition-all"
      style={{
        background: 'linear-gradient(135deg, rgba(249,115,22,0.08), rgba(245,158,11,0.04))',
        border: '1px solid rgba(249,115,22,0.15)',
      }}
    >
      <div className="flex items-center justify-between">
        <Link to={hasPending ? '/challenges' : '/challenges/new'} className="flex items-center gap-3 flex-1 group">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, #f97316, #f59e0b)' }}
          >
            <Trophy size={18} className="text-white" />
          </div>
          <div>
            <p className="text-sm font-bold">Ready to prove your strategy?</p>
            <p className="text-xs text-ink-3">{ctaText}</p>
          </div>
        </Link>
        {hasChallenges && (
          <Link to="/challenges" className="text-[10px] text-ink-4 hover:text-orange transition-colors flex items-center gap-1 ml-3 flex-shrink-0">
            View All <ArrowRight size={14} />
          </Link>
        )}
        {!hasChallenges && (
          <Link to="/challenges/new">
            <ArrowRight size={16} className="text-ink-4 hover:text-orange transition-colors" />
          </Link>
        )}
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="glass-sm rounded-lg p-10 text-center">
      <Bot size={40} className="text-orange/40 mx-auto mb-3" />
      <h3 className="text-sm font-bold mb-1">No bots yet</h3>
      <p className="text-xs text-ink-3 mb-4">
        Create a strategy and deploy your first trading bot.
      </p>
      <div className="flex items-center justify-center gap-3">
        <Link to="/strategies/new/upload">
          <Button size="sm">
            <Plus size={14} />
            New Strategy
          </Button>
        </Link>
        <Link to="/bots/new">
          <Button variant="ghost" size="sm">
            Create Bot
          </Button>
        </Link>
      </div>
    </div>
  );
}
